import { Brain, Zap, ShieldCheck, FileLock2, Workflow, BarChart3, ArrowUpRight } from "lucide-react";

/**
 * Capabilities-led value proposition for /for-brands.
 *
 * Replaces a previous social-proof section that contained fabricated case
 * studies and testimonials. Trust > unverifiable claims at this stage —
 * we'll re-introduce real customer stories only when they're attributable,
 * consented, and substantiated.
 */
const CAPABILITIES = [
  {
    icon: Brain,
    title: "AI matcher with reasoning",
    desc: "Brief PITCHD with one paragraph. The matcher ranks the athlete network by audience fit and explains *why* each one matches — not just a similarity score, an editor's note.",
  },
  {
    icon: Zap,
    title: "From brief to outreach in minutes",
    desc: "Generate shortlists, write opening messages, and send via the in-app inbox without ever leaving the workspace or touching a spreadsheet.",
  },
  {
    icon: Workflow,
    title: "Pipeline + replies in one place",
    desc: "Conversations, contracts, deliverables and ROI tracking sit alongside every shortlist — so two-person teams can run what would normally need an agency.",
  },
  {
    icon: BarChart3,
    title: "Campaign reporting that audits cleanly",
    desc: "Every send, reply, view and acceptance logged automatically. Export at any time for finance, legal, or board reporting.",
  },
  {
    icon: ShieldCheck,
    title: "GDPR + audit trail by default",
    desc: "Per-seat permissions, role-based access, full activity audit log, and athlete consent on every outbound message. Procurement-friendly from day one.",
  },
  {
    icon: FileLock2,
    title: "Athlete data, on athletes' terms",
    desc: "Athletes control their own profiles and reply preferences. PITCHD doesn't sell or resell rosters — your shortlists stay private to your workspace.",
  },
];

export default function BrandSocialProof() {
  return (
    <section id="capabilities" className="py-24 border-t border-line" data-testid="brand-capabilities">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="label-eyebrow">Why brand teams choose PITCHD</div>
        <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-4 max-w-3xl">
          A workspace built for direct, measurable athlete partnerships.
        </h2>
        <p className="text-ink-muted text-sm md:text-base max-w-2xl mt-4 mb-12 leading-relaxed">
          PITCHD replaces the agency back-and-forth with a single workspace where you brief, match, message, sign and report. Built around the workflows brand managers actually run weekly.
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          {CAPABILITIES.map((c, i) => {
            const Icon = c.icon;
            return (
              <article key={i} className="surface p-6 md:p-7 flex gap-5" data-testid={`brand-capability-${i}`}>
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-volt/15 border border-volt/40 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-volt" />
                  </div>
                </div>
                <div className="min-w-0">
                  <h3 className="font-heading text-lg uppercase tracking-tight">{c.title}</h3>
                  <p className="text-sm text-ink-muted leading-relaxed mt-2">{c.desc}</p>
                </div>
              </article>
            );
          })}
        </div>

        <div className="mt-12 surface p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-4 md:gap-8" data-testid="brand-capabilities-cta">
          <div className="flex-1">
            <div className="label-eyebrow text-volt">Founding brand programme</div>
            <h3 className="font-heading text-xl md:text-2xl uppercase tracking-tight mt-2">Be one of the first brands shaping how PITCHD works.</h3>
            <p className="text-sm text-ink-muted leading-relaxed mt-2">
              We&apos;re working closely with a small set of early brand partners to refine the matcher, the contract flow, and the reporting export. If you&apos;d like to be one of them, request a demo and we&apos;ll be in touch.
            </p>
          </div>
          <a href="#demo" className="btn-volt inline-flex items-center gap-2 whitespace-nowrap" data-testid="brand-capabilities-demo-btn">
            Request demo <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
